import type { Router } from 'vue-router'
import { Device, UserService, Store } from 'app-core'
import type { IIntegrationsMap } from 'app-core'
import i18n from '@/i18n'
import { anyDirty, anySaving, clearRelease, releaseChanges } from '@/composables/useDirtyGuard'
import { useConfirm } from '@/composables/useConfirm'
import { handleSheetBack } from '@/utilities/sheetRegistry'
import { integrationEnabled } from '@/utilities/integrations'
import { forgetListLocations } from '@/utilities/listQuery'

const { confirm } = useConfirm()

/** The routes a lost session lands on: nothing can be saved any more, so nothing is asked. */
const SESSION_ROUTES = new Set(['tenantLogin', 'userLogin'])

/** The unsaved-changes question — resolves true when the user chooses to leave. */
async function askToLeave(): Promise<boolean> {
    const { t } = i18n.global
    const answer = await confirm(t('document.leave_text'), {
        title: t('document.leave_title'),
        buttonTrueText: t('document.leave_confirm'),
        buttonCancelText: t('document.stay')
    })
    return answer === true
}

/**
 * Before leaving the app or the session (logout, the jump to the POS or the kitchen): the
 * same question as the route guard while a screen holds unsaved changes, asked BEFORE the
 * logout runs so "Ostani" keeps a working session. A save still in flight keeps the user
 * here. Once the user agrees, the route guard and the browser let the exit through.
 */
export async function confirmLeave(): Promise<boolean> {
    if (anySaving()) return false
    if (!anyDirty()) return true
    if (!await askToLeave()) return false
    releaseChanges()
    return true
}

/**
 * 1:1 port of the old admin guard chain (apps/admin/src/router/router.js beforeEach).
 *
 * The old app deferred ALL navigation behind a `continueExecution` promise resolved after
 * AppService.initializeAdminApp(). In admin the router is installed only AFTER init
 * completes (sap boot pattern), so no guard ever runs against uninitialized state — same
 * observable behavior, simpler mechanism.
 *
 * The `next()` callback style is kept intentionally (still supported in vue-router 5) to
 * preserve the exact `next(false)` abort semantics of the original.
 */
export function installGuards(router: Router) {
    // AR-26 — mobile back-button closes overlays instead of navigating (the
    // web twin of POS App.vue's Capacitor backButton order). A history
    // navigation (browser/hardware back) while an overlay is open is consumed:
    // the top CodSheet pops a step, otherwise the topmost dismissable layer
    // gets Escape (reka closes modals/drawers in nesting order, respecting
    // per-dialog guards). Returning false on a popstate navigation makes
    // vue-router restore the URL — no pushState choreography needed.
    // Traversal detection compares vue-router's own `history.state.position`
    // stamp against the last committed one (a `popstate` listener races the
    // router's internal listener and misses the first back — hence no flag).
    let committedPosition: number | undefined = window.history.state?.position

    router.afterEach((to) => {
        committedPosition = window.history.state?.position ?? committedPosition
        // An agreed exit is spent once a navigation lands.
        clearRelease()
        // The session is gone: the next one opens the lists bare, not as this one left them.
        if (SESSION_ROUTES.has(String(to.name))) forgetListLocations()
    })

    // After an aborted traversal vue-router restores the URL asynchronously
    // (history.go back to the committed entry) — resync the stamp once that
    // settles so the next back is detected correctly.
    const resyncAfterAbort = () => {
        setTimeout(() => {
            committedPosition = window.history.state?.position ?? committedPosition
        }, 100)
    }

    router.beforeEach(() => {
        const target = window.history.state?.position
        const isTraversal = typeof target === 'number'
            && typeof committedPosition === 'number'
            && target !== committedPosition
        if (!isTraversal) return true

        if (handleSheetBack()) {
            resyncAfterAbort()
            return false
        }

        if (document.querySelector('div[role="dialog"]')) {
            document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }))
            resyncAfterAbort()
            return false
        }

        return true
    })

    // Unsaved changes: leaving a screen that holds changes asks first. Registered after the
    // back-button guard on purpose — a Back press while a dialog is open only closes the
    // dialog and never reaches this question. A change of query alone stays on the screen.
    router.beforeEach(async (to, from) => {
        if (to.path === from.path) return true
        // The session is gone (logout asked first; a rejected token cannot save anyway).
        if (SESSION_ROUTES.has(String(to.name))) return true

        // A save of those changes is in flight: the screen stays until it answers, then
        // leaves on its own or shows the error (the old step dialogs blocked the page too).
        if (anySaving()) {
            resyncAfterAbort()
            return false
        }

        if (!anyDirty()) return true
        if (await askToLeave()) return true

        // A refused Back leaves the URL to be restored, like the dialog case above.
        resyncAfterAbort()
        return false
    })

    // Reload and tab close: the browser shows its own question (no custom text allowed).
    // Not in the desktop app: Electron shows nothing for it and silently cancels the unload —
    // the POS window could then neither leave the admin nor quit.
    window.addEventListener('beforeunload', (event) => {
        if (Device.isElectron() || !anyDirty()) return
        event.preventDefault()
        // Deprecated, but browsers that predate the preventDefault() form still need it.
        event.returnValue = ''
    })

    router.beforeEach(async (to, _from, next) => {
        const tenant = Store.get<any>('tenant')
        const user = Store.get<any>('user')

        if (
            !tenant
            && to.name !== 'tenantLogin'
            && to.name !== 'invoiceScan'
        ) {
            return next({ name: 'tenantLogin' })
        }

        if (tenant && to.name === 'tenantLogin') {
            if (!user) {
                return next({ name: 'userLogin' })
            }

            const canAccessAdmin = UserService.allows('nav_access_admin')
            if (canAccessAdmin) {
                return next({ name: 'adminDashboard' })
            }

            return next(false)
        }

        const requiredPermissions = to.matched.flatMap(route => route.meta.requiredPermission ?? [])
        if (!UserService.allows(requiredPermissions)) {
            return next({ name: 'userLogin' })
        }

        const enabledWithModules = to.matched
            .map(route => route.meta.enabledWithModules)
            .filter((p): p is string[] => !!p)
            .flat()

        if (enabledWithModules.length) {
            const modulesEnabled = enabledWithModules.map((mod) => {
                if (mod === 'inventory_accounting') return !!tenant.venue?.settings?.use_inventory_accounting
                return !!Store.get<Record<string, any>>('modules')?.[mod]
            })

            if (!modulesEnabled.some((enabled: boolean) => enabled == true)) {
                return next(false)
            }
        }

        // Owner ruling D8: while the venue keeps inventory accounting, delivery notes come from the
        // accounting documents — their create / edit pages send the user to the list (an `:id`
        // opens that document's preview there). The server guard is a separate Backlog item.
        const lockedTo = to.matched.map(route => route.meta.inventoryAccountingLocked).find(Boolean)
        if (lockedTo && tenant.venue?.settings?.use_inventory_accounting) {
            return next({ name: lockedTo, query: to.params.id ? { open: String(to.params.id) } : {} })
        }

        const requiredIntegrations = to.matched
            .map(route => route.meta.requiredIntegration)
            .filter((p): p is string => !!p)

        if (requiredIntegrations.length) {
            const integrations = Store.get<IIntegrationsMap>('integrations')
            const allEnabled = requiredIntegrations.every(name => integrationEnabled(integrations, name))
            if (!allEnabled) {
                return next({ name: 'adminDashboard' })
            }
        }

        return next()
    })
}
